import type { FC, ReactNode, SVGAttributes } from "react";

export type IconProps = {
  size?: number;
  sw?: number;
  className?: string;
};

type SvgProps = IconProps & {
  fill?: string;
  children?: ReactNode;
} & Omit<SVGAttributes<SVGSVGElement>, "size" | "fill" | "children">;

const Svg: FC<SvgProps> = ({
  size = 22,
  sw = 1.8,
  fill,
  className,
  children,
  ...rest
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill ?? "none"}
    stroke={fill ? "none" : "currentColor"}
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...rest}
  >
    {children}
  </svg>
);

export const Icons = {
  badge: (p) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </Svg>
  ),
  list: (p) => (
    <Svg {...p}>
      <path d="M8 6h12M8 12h12M8 18h12" />
      <circle cx="4" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4" cy="18" r="1.2" fill="currentColor" stroke="none" />
    </Svg>
  ),
  cal: (p) => (
    <Svg {...p}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </Svg>
  ),
  user: (p) => (
    <Svg {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </Svg>
  ),
  play: (p) => (
    <Svg {...p}>
      <path d="M7 5l12 7-12 7V5z" fill="currentColor" />
    </Svg>
  ),
  pause: (p) => (
    <Svg {...p}>
      <rect
        x="6"
        y="5"
        width="4"
        height="14"
        rx="1"
        fill="currentColor"
        stroke="none"
      />
      <rect
        x="14"
        y="5"
        width="4"
        height="14"
        rx="1"
        fill="currentColor"
        stroke="none"
      />
    </Svg>
  ),
  stop: (p) => (
    <Svg {...p}>
      <rect
        x="6"
        y="6"
        width="12"
        height="12"
        rx="2"
        fill="currentColor"
        stroke="none"
      />
    </Svg>
  ),
  coffee: (p) => (
    <Svg {...p}>
      <path d="M4 8h14v6a5 5 0 01-5 5H9a5 5 0 01-5-5V8z" />
      <path d="M18 10h2a2 2 0 010 4h-2" />
      <path d="M8 3v2M12 3v2" />
    </Svg>
  ),
  check: (p) => (
    <Svg {...p}>
      <path d="M5 12l5 5L20 7" />
    </Svg>
  ),
  arrowIn: (p) => (
    <Svg {...p}>
      <path d="M3 12h14M11 6l6 6-6 6" />
    </Svg>
  ),
  arrowOut: (p) => (
    <Svg {...p}>
      <path d="M21 12H7M13 6l-6 6 6 6" />
    </Svg>
  ),
  warn: (p) => (
    <Svg {...p}>
      <path d="M12 4l9 16H3l9-16z" />
      <path d="M12 10v4M12 17v.5" />
    </Svg>
  ),
  plus: (p) => (
    <Svg {...p}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  ),
  chev: (p) => (
    <Svg {...p}>
      <path d="M9 6l6 6-6 6" />
    </Svg>
  ),
  chevL: (p) => (
    <Svg {...p}>
      <path d="M15 6l-6 6 6 6" />
    </Svg>
  ),
  chevD: (p) => (
    <Svg {...p}>
      <path d="M6 9l6 6 6-6" />
    </Svg>
  ),
  close: (p) => (
    <Svg {...p}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Svg>
  ),
  search: (p) => (
    <Svg {...p}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </Svg>
  ),
  pin: (p) => (
    <Svg {...p}>
      <path d="M12 2c-3.9 0-7 3.1-7 7 0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z" />
      <circle cx="12" cy="9" r="2.5" />
    </Svg>
  ),
  pinOff: (p) => (
    <Svg {...p}>
      <path
        d="M12 2c-3.9 0-7 3.1-7 7 0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"
        strokeDasharray="3 3"
      />
    </Svg>
  ),
  mobile: (p) => (
    <Svg {...p}>
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M10 18h4" />
    </Svg>
  ),
  borne: (p) => (
    <Svg {...p}>
      <rect x="4" y="4" width="16" height="14" rx="2" />
      <path d="M9 21h6M8 9h8M8 13h5" />
    </Svg>
  ),
  sync: (p) => (
    <Svg {...p}>
      <path d="M3 12a9 9 0 0115-6.7L21 8" />
      <path d="M21 4v4h-4" />
      <path d="M21 12a9 9 0 01-15 6.7L3 16" />
      <path d="M3 20v-4h4" />
    </Svg>
  ),
  cloud: (p) => (
    <Svg {...p}>
      <path d="M7 18h10a4 4 0 000-8 6 6 0 00-11.6-2A4 4 0 007 18z" />
    </Svg>
  ),
  bell: (p) => (
    <Svg {...p}>
      <path d="M6 8a6 6 0 0112 0v5l2 3H4l2-3V8z" />
      <path d="M10 19a2 2 0 004 0" />
    </Svg>
  ),
  lock: (p) => (
    <Svg {...p}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Svg>
  ),
  shield: (p) => (
    <Svg {...p}>
      <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
    </Svg>
  ),
  out: (p) => (
    <Svg {...p}>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </Svg>
  ),
  briefcase: (p) => (
    <Svg {...p}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M3 13h18" />
    </Svg>
  ),
  invoice: (p) => (
    <Svg {...p}>
      <path d="M6 3h9l3 3v15H6z" />
      <path d="M9 9h6M9 13h6M9 17h3" />
    </Svg>
  ),
  tractor: (p) => (
    <Svg {...p}>
      <circle cx="7" cy="17" r="3.5" />
      <circle cx="17" cy="18" r="2.5" />
      <path d="M3.5 17h-.5v-4h6l1-4h6l2 4h2v4h-3" />
    </Svg>
  ),
  tools: (p) => (
    <Svg {...p}>
      <path d="M14.7 6.3a4 4 0 00-5.4 5.4l-6 6 2 2 6-6a4 4 0 005.4-5.4l-2 2-2-2 2-2z" />
    </Svg>
  ),
  truck: (p) => (
    <Svg {...p}>
      <path d="M3 6h11v10H3zM14 10h4l3 3v3h-7" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17.5" cy="18" r="2" />
    </Svg>
  ),
  edit: (p) => (
    <Svg {...p}>
      <path d="M4 20l4-1 11-11-3-3L5 16l-1 4z" />
    </Svg>
  ),
  globe: (p) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
    </Svg>
  ),
  chat: (p) => (
    <Svg {...p}>
      <path d="M4 5h16v11H8l-4 4V5z" />
    </Svg>
  ),
  home: (p) => (
    <Svg {...p}>
      <path d="M4 11l8-7 8 7v9a1 1 0 01-1 1h-4v-6H9v6H5a1 1 0 01-1-1v-9z" />
    </Svg>
  ),
  team: (p) => (
    <Svg {...p}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
      <path d="M15.5 4.6a3.5 3.5 0 010 6.8M17.5 13.8c2.3.8 4 3 4 5.7" />
    </Svg>
  ),
  more: (p) => (
    <Svg {...p}>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </Svg>
  ),
} satisfies Record<string, FC<IconProps>>;
